<template>
  <div class="table-module">
    <div class="table-header">
      {{ dashboardModule.name }}

      <div class="actions">
        <SnapQLDebugButton
          :dashboard-module="dashboardModule"
          :dashboard-data-context="dashboardDataContext"
          :dashboard-key="dashboardKey" />
        <ExportButton
          :dashboard-module="dashboardModule"
          :dashboard-data-context="moduleDataContext"
          :dashboard-key="dashboardKey" />
        <ShareButton
          :dashboard-module="dashboardModule"
          :dashboard-key="dashboardKey"
          :shared-module-id="sharedModuleId"
          @shared="$emit('module-shared', { moduleKey: dashboardModule.key, sharedModuleId: $event })"
          @unshared="$emit('module-unshared', { moduleKey: dashboardModule.key })" />
      </div>
    </div>
    <div
      v-if="rowLimitHit"
      class="row-limit-notice">
      <span>Too much data to display in the browser. Use the 'Download CSV' button to retrieve the full data set.</span>
      <button
        class="close-btn"
        @click="rowLimitHit = false">
        Dismiss
      </button>
    </div>
    <b-skeleton
      v-if="isLoading"
      height="400px"
      style="border-radius: 0"
      width="100%"
      :active="isLoading" />
    <div
      v-else-if="queryError"
      class="query-error">
      <b-icon
        icon="alert-circle-outline"
        size="is-small" />
      <span>{{ queryError }}</span>
    </div>
    <div
      v-else
      class="table-container">
      <table-column
        v-for="(column, i) in data.columns"
        :key="column.id"
        :class="{ 'sticky-column': i === 0 && !$screen.is('sm') }"
        :current-depth="0"
        :column="column"
        :rows="rows"
        :table-sort.sync="sort" />
    </div>
  </div>
</template>

<script>
  import ExportButton from '../ExportButton.vue'
  import SnapQLDebugButton from '../SnapQLDebugButton.vue'
  import ShareButton from '../ShareButton.vue'

  import TableColumn from './TableColumn.vue'
  import axios from 'axios'
  import { orderBy } from 'lodash'
  export default {
    name: 'TableModule',
    components: {
      TableColumn, ExportButton, SnapQLDebugButton, ShareButton,
    },

    provide () {
      return {
        getMaxDepth: () => this.maxDepth,
      }
    },
    props: {
      isReady: {
        type: Boolean,
        required: true,
      },
      dashboardModule: {
        type: Object,
        required: true,
      },
      dashboardDataContext: {
        type: Object,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
      sharedModuleId: {
        type: String,
        default: undefined,
      },
    },
    data () {
      return {
        data: {
          type: '',
          id: '',
          key: '',
          name: '',
          rows: [],
          columns: [],
          depth: 2,
        },
        sort: {},
        isLoading: false,
        queryError: null,
        currentRequest: null,
        rowLimitHit: false,
      }
    },
    computed: {
      maxDepth () {
        return this.data.depth
      },
      moduleDataContext () {
        if (this.sort.rowIdx != undefined) {
          return {
            ...this.dashboardDataContext,
            module_sort: {
              sort_by: this.sort.rowIdx,
              ascending: this.sort.ascending,
            },
          }
        } else {
          return this.dashboardDataContext
        }
      },
      rows () {
        let rows = []
        if (this.sort.rowIdx != undefined) {
          const sortedRows = orderBy(
            this.data.rows,
            [
              (row) => row.cells[this.sort.rowIdx].sort_by,
            ],
            [
              this.sort.ascending ? 'asc': 'desc',
            ]
          )
          const groupMins = []
          sortedRows.forEach((row) => {
            row.sort_groups.forEach((group, idx) => {
              if (idx > groupMins.length - 1) {
                groupMins.push({})
              }
              if (groupMins[idx][row.sort_groups.slice(0,idx + 1).join('-')] == undefined) {
                groupMins[idx][row.sort_groups.slice(0,idx + 1).join('-')] = row.cells[this.sort.rowIdx].sort_by
              }
            })
          })
          rows = orderBy(
            this.data.rows,
            groupMins.flatMap((group, idx) =>
              [
                (row) => row.cells[idx].is_total,
                (row) => group[row.sort_groups.slice(0,idx + 1).join('-')] == null,
                (row) => group[row.sort_groups.slice(0,idx + 1).join('-')],
                (row) => row.sort_groups[idx],
              ]
            ),
            groupMins.flatMap(() =>
              [
                'desc',
                !this.sort.ascending ? 'asc': 'desc',
                this.sort.ascending ? 'asc': 'desc',
                'asc',
              ]
            )
          )
        } else {
          rows = this.data.rows
        }
        let columnRows = {}
        rows.forEach(row => {
          row.cells.forEach((cell, idx) => {
            if (columnRows[idx] == undefined) {
              columnRows[idx] = []
            }
            columnRows[idx].push(cell)
          })
        })
        return columnRows
      },
    },
    watch: {
      dashboardDataContext: {
        handler () {
          this.loadData()
        },
        deep: true,
      },
    },
    mounted () {
      this.loadData()
    },
    beforeDestroy () {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Component destroyed')
      }
      if (this.abortController) {
        this.abortController.abort()
      }
    },
    methods: {
      getRowCell (row, column) {
        return this.data.rows[row][column]
      },
      async loadData () {
        if (!this.isReady) {
          return
        }
        const useSnapQL = this.$hasFeatureFlag('USE_SNAPQL')
        if (useSnapQL) {
          return this.loadDataStreaming()
        }
        try {
          if (this.currentRequest) {
            this.currentRequest.cancel('New request initiated')
          }

          this.currentRequest = axios.CancelToken.source()
          this.isLoading = true
          this.queryError = null
          this.rowLimitHit = false

          const response = await this.$apis.companyConfigs.getDashboardModuleData(
            this.dashboardKey,
            this.dashboardModule.key,
            this.dashboardDataContext,
            { cancelToken: this.currentRequest.token, }
          )
          if (response?.error) {
            if (response?.code !== 'ERR_CANCELED') {
              this.$buefy.toast.open({
                message: 'Failed to load Chart Data',
                type: 'is-warning',
              })
            }
            return
          }
          this.data = response
          this.isLoading = false
          this.sort = {}
          this.rowLimitHit = !!response.row_limit_hit
        } catch (err) {
          console.error('Error loading data:', err)
        }
      },
      async loadDataStreaming () {
        if (this.abortController) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        this.isLoading = true
        this.queryError = null
        this.rowLimitHit = false
        this.data.rows = []
        this.data.columns = []
        this.sort = {}

        await this.$apis.companyConfigs.streamDashboardModuleData(
          this.dashboardKey,
          this.dashboardModule.key,
          this.dashboardDataContext,
          {
            columns: (payload) => {
              this.data.columns = payload.columns
              this.data.name = payload.name
              this.data.depth = payload.depth
            },
            rows: (rows) => {
              this.data.rows = this.data.rows.concat(rows)
            },
            complete: () => {
              this.isLoading = false
            },
            truncated: () => {
              this.rowLimitHit = true
              this.isLoading = false
            },
            error: (err) => {
              this.queryError = err.message || 'Failed to load table data'
              this.isLoading = false
            },
          },
          this.abortController.signal
        )
      },
    },
  }
</script>

<style scoped>
.table-module {
  border: 1px solid #e9e9ea;
  background-color: white;
  border-radius: 4px;
  overflow: visible;
}
.row-limit-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: rgb(0, 0, 0);
  color: rgb(255, 255, 255);
  font-size: 14px;
}
.row-limit-notice .close-btn {
  background: none;
  border: none;
  color: #F6C856;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  margin-left: 12px;
  white-space: nowrap;
}
.table-container {
  display: flex;
  border-top: 1px solid #e9e9ea;
  overflow-x: auto;
  overflow-y: visible;
  font-size: 12px;
  position: relative;
}
.table-header {
  padding: 12px;
  font-size: 16px;
  color: #333;
  padding: 8px;
  display: flex;
  align-items: center;

  .actions {
    margin-left: auto;

    .mdi-download {
      font-size: 18px;
    }
  }
}
.query-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px;
  color: #b5451b;
  font-size: 13px;
  border-top: 1px solid #e9e9ea;
}
.sticky-column {
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: #fff;
}
</style>
